import { SECTION_ORDER } from '../constants';
import type { Condition, Question, RepeatingGroupSchema, Section } from '../types';

// ---------------------------------------------------------------------------
// Condition evaluation — module-level so repeating-group rows can reuse it
// ---------------------------------------------------------------------------

/** Evaluate a single condition against an answers/row object */
export function evaluateCondition(condition: Condition, answers: Record<string, unknown>): boolean {
  const actual = answers[condition.field];

  switch (condition.operator) {
    case 'exists':
      return actual !== undefined && actual !== null && actual !== '';
    case 'not_exists':
      return actual === undefined || actual === null || actual === '';
    case 'eq':
      return actual === condition.value;
    case 'neq':
      return actual !== condition.value;
    case 'in':
      if (Array.isArray(condition.value)) {
        return condition.value.includes(actual);
      }
      return false;
    case 'not_in':
      if (Array.isArray(condition.value)) {
        return !condition.value.includes(actual);
      }
      return true;
    case 'contains':
      if (Array.isArray(actual)) {
        return actual.includes(condition.value);
      }
      if (typeof actual === 'string' && typeof condition.value === 'string') {
        return actual.includes(condition.value);
      }
      return false;
    case 'gt':
      return (
        typeof actual === 'number' &&
        typeof condition.value === 'number' &&
        actual > condition.value
      );
    case 'gte':
      return (
        typeof actual === 'number' &&
        typeof condition.value === 'number' &&
        actual >= condition.value
      );
    case 'lt':
      return (
        typeof actual === 'number' &&
        typeof condition.value === 'number' &&
        actual < condition.value
      );
    case 'lte':
      return (
        typeof actual === 'number' &&
        typeof condition.value === 'number' &&
        actual <= condition.value
      );
    default:
      return false;
  }
}

/** AND logic — every condition must pass */
export function evaluateConditionList(
  conditions: Condition[],
  answers: Record<string, unknown>,
): boolean {
  return conditions.every((c) => evaluateCondition(c, answers));
}

// ---------------------------------------------------------------------------
// Repeating-group helpers
// ---------------------------------------------------------------------------
// A repeating_group answer is an array of row objects keyed by sub-question
// field_path. Sub-question conditions are evaluated against the SAME row, with
// condition.field referencing a sibling sub-question's field_path.
// ---------------------------------------------------------------------------

export type RepeatingGroupRow = Record<string, unknown>;

/** Sub-questions visible for a given row after condition evaluation */
export function getVisibleRowFields(
  schema: RepeatingGroupSchema,
  row: RepeatingGroupRow,
): Question[] {
  return schema.fields.filter((f) => evaluateConditionList(f.conditions, row));
}

function isEmptyValue(v: unknown): boolean {
  return v === undefined || v === null || v === '';
}

/**
 * Count required sub-fields (total / answered) across all rows of a
 * repeating-group answer. Used by progress + completeness scoring.
 */
export function countRepeatingGroupFields(
  question: Question,
  value: unknown,
): { total: number; answered: number } {
  const schema = question.repeating_group;
  if (!schema) return { total: 0, answered: 0 };

  const rows: RepeatingGroupRow[] = Array.isArray(value) ? (value as RepeatingGroupRow[]) : [];

  // Baseline = required sub-fields visible on an empty row (for not-yet-added
  // rows below min_rows).
  const baselineRequired = getVisibleRowFields(schema, {}).filter((f) => f.required).length;

  let total = 0;
  let answered = 0;

  for (const row of rows) {
    for (const f of getVisibleRowFields(schema, row)) {
      if (!f.required) continue;
      total++;
      if (!isEmptyValue(row[f.field_path])) answered++;
    }
  }

  // Rows still owed below the minimum count as fully unanswered.
  if (rows.length < schema.min_rows) {
    total += (schema.min_rows - rows.length) * Math.max(baselineRequired, 1);
  }

  // Required group with min_rows 0: an explicit (possibly empty) array counts
  // as the single "answered" unit.
  if (question.required && total === 0) {
    total = 1;
    answered = Array.isArray(value) ? 1 : 0;
  }

  return { total, answered };
}

/**
 * Validate a repeating-group answer. Returns a user-facing error message, or
 * null when the answer is valid.
 */
export function validateRepeatingGroupAnswer(question: Question, value: unknown): string | null {
  const schema = question.repeating_group;
  if (!schema) return null;

  const rows: RepeatingGroupRow[] = Array.isArray(value) ? (value as RepeatingGroupRow[]) : [];

  if (rows.length < schema.min_rows) {
    const noun = schema.row_label.toLowerCase();
    return schema.min_rows === 1
      ? `Please add at least one ${noun}.`
      : `Please add at least ${schema.min_rows} ${noun}s.`;
  }

  if (schema.max_rows !== undefined && rows.length > schema.max_rows) {
    return `Please limit this to ${schema.max_rows} ${schema.row_label.toLowerCase()}s. Contact us if you need more.`;
  }

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    for (const f of getVisibleRowFields(schema, row)) {
      if (f.required && isEmptyValue(row[f.field_path])) {
        return `${schema.row_label} ${i + 1}: "${f.text}" is required.`;
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// ConversationEngine — stateful question-flow controller
// ---------------------------------------------------------------------------

export type EngineMode = 'prospect' | 'agent';

export interface EngineState {
  /** Index into the sorted active-question list */
  currentIndex: number;
  /** All answers keyed by question id */
  answers: Record<string, unknown>;
  /** Ordered history of answered question ids (for back()) */
  history: string[];
  /** Currently visible questions after condition evaluation */
  activeQuestions: Question[];
}

export class ConversationEngine {
  private readonly questions: Question[];
  private readonly mode: EngineMode;
  private state: EngineState;

  constructor(questions: Question[], mode: EngineMode = 'prospect') {
    this.questions = this.sortQuestions(questions);
    this.mode = mode;
    this.state = {
      currentIndex: 0,
      answers: {},
      history: [],
      activeQuestions: [],
    };
    // Initial evaluation with no answers — shows unconditional questions
    this.state.activeQuestions = this.evaluateActiveQuestions(this.state.answers);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /** Return the current question, or null if the flow is complete */
  getCurrentQuestion(): Question | null {
    return this.state.activeQuestions[this.state.currentIndex] ?? null;
  }

  /** Store an answer, re-evaluate visible questions, advance to the next one */
  answer(questionId: string, value: unknown): Question | null {
    this.state.answers[questionId] = value;
    this.state.history.push(questionId);

    // Re-evaluate which questions are active after the new answer
    this.state.activeQuestions = this.evaluateActiveQuestions(this.state.answers);

    // Clean up answers for questions that are no longer visible
    this.clearHiddenAnswers();

    // Find where we are now (the question we just answered may have shifted)
    const justAnsweredIdx = this.state.activeQuestions.findIndex((q) => q.id === questionId);
    this.state.currentIndex =
      justAnsweredIdx >= 0 ? justAnsweredIdx + 1 : this.state.currentIndex + 1;

    return this.getCurrentQuestion();
  }

  /** Go back to the previous question in history */
  back(): Question | null {
    if (this.state.history.length === 0) return this.getCurrentQuestion();

    const prevId = this.state.history.pop()!;
    const prevIdx = this.state.activeQuestions.findIndex((q) => q.id === prevId);
    if (prevIdx >= 0) {
      this.state.currentIndex = prevIdx;
    } else {
      // The previous question is no longer visible; try the one before it
      this.state.currentIndex = Math.max(0, this.state.currentIndex - 1);
    }
    return this.getCurrentQuestion();
  }

  /** Remove stored answers for questions that are no longer active */
  clearHiddenAnswers(): void {
    const activeIds = new Set(this.state.activeQuestions.map((q) => q.id));
    // Only clear keys that belong to known questions which are now hidden.
    // Meta answer keys that aren't question ids (e.g. industry_type, set by
    // the classifier / prefill) must persist — supplements gate on them.
    const knownIds = new Set(this.questions.map((q) => q.id));
    for (const key of Object.keys(this.state.answers)) {
      if (knownIds.has(key) && !activeIds.has(key)) {
        delete this.state.answers[key];
      }
    }
  }

  /** Return all current answers as a nested object using field_path mapping */
  getSubmissionData(): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    for (const q of this.state.activeQuestions) {
      const val = this.state.answers[q.id];
      if (val !== undefined) {
        setNestedValue(data, q.field_path, val);
      }
    }
    return data;
  }

  /** Flat answers map (keyed by question id) */
  getAnswers(): Record<string, unknown> {
    return { ...this.state.answers };
  }

  /** Calculate completion progress */
  getProgress(): {
    percentage: number;
    answered: number;
    total: number;
    bySection: Record<string, { answered: number; total: number; percentage: number }>;
  } {
    const active = this.state.activeQuestions;

    // Each plain required question counts as 1 unit. A repeating_group counts
    // each required sub-field per row (see countRepeatingGroupFields).
    const countQuestion = (q: Question): { total: number; answered: number } => {
      if (q.field_type === 'repeating_group') {
        return countRepeatingGroupFields(q, this.state.answers[q.id]);
      }
      if (!q.required) return { total: 0, answered: 0 };
      return {
        total: 1,
        answered: this.state.answers[q.id] !== undefined ? 1 : 0,
      };
    };

    let total = 0;
    let answered = 0;
    const bySection: Record<string, { answered: number; total: number; percentage: number }> = {};

    for (const section of SECTION_ORDER) {
      let sectionTotal = 0;
      let sectionAnswered = 0;
      for (const q of active) {
        if (q.section !== section) continue;
        const c = countQuestion(q);
        sectionTotal += c.total;
        sectionAnswered += c.answered;
      }
      if (sectionTotal === 0) continue;
      total += sectionTotal;
      answered += sectionAnswered;
      bySection[section] = {
        answered: sectionAnswered,
        total: sectionTotal,
        percentage: Math.round((sectionAnswered / sectionTotal) * 100),
      };
    }

    return {
      percentage: total > 0 ? Math.round((answered / total) * 100) : 100,
      answered,
      total,
      bySection,
    };
  }

  /** Get the full list of currently active questions */
  getActiveQuestions(): Question[] {
    return [...this.state.activeQuestions];
  }

  /** Get engine mode */
  getMode(): EngineMode {
    return this.mode;
  }

  /** Bulk-load answers (e.g. from a saved draft) without advancing */
  loadAnswers(answers: Record<string, unknown>): void {
    this.state.answers = { ...answers };
    this.state.activeQuestions = this.evaluateActiveQuestions(this.state.answers);
    this.clearHiddenAnswers();
    // Position at first unanswered required question
    const firstUnanswered = this.state.activeQuestions.findIndex(
      (q) => q.required && this.state.answers[q.id] === undefined,
    );
    this.state.currentIndex =
      firstUnanswered >= 0 ? firstUnanswered : this.state.activeQuestions.length;
  }

  // -------------------------------------------------------------------------
  // Condition evaluation
  // -------------------------------------------------------------------------

  /** Return only the questions whose conditions are all satisfied */
  evaluateActiveQuestions(answers: Record<string, unknown>): Question[] {
    return this.questions.filter((q) => {
      // Mode gating — e.g. detail questions the prospect flow gathers from
      // uploaded documents are marked modes: ['agent'].
      if (q.modes && !q.modes.includes(this.mode)) return false;
      if (q.conditions.length === 0) return true;
      return this.evaluateConditions(q.conditions, answers);
    });
  }

  /** AND logic — every condition must pass */
  evaluateConditions(conditions: Condition[], answers: Record<string, unknown>): boolean {
    return evaluateConditionList(conditions, answers);
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private sortQuestions(questions: Question[]): Question[] {
    return [...questions].sort((a, b) => {
      const secA = SECTION_ORDER.indexOf(a.section);
      const secB = SECTION_ORDER.indexOf(b.section);
      if (secA !== secB) return secA - secB;
      return a.sort_order - b.sort_order;
    });
  }
}

// ---------------------------------------------------------------------------
// Dot-path helpers — "business.address.city" → nested object access
// ---------------------------------------------------------------------------

export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (
      current[part] === undefined ||
      current[part] === null ||
      typeof current[part] !== 'object'
    ) {
      current[part] = {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}
